package com.example.monetix.ui.screens

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Visibility
import androidx.compose.material.icons.outlined.VisibilityOff
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.sp
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.launch

@Composable
fun LoginScreen(onLoginSuccess: () -> Unit) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }

    var isLoginMode by remember { mutableStateOf(true) }
    var passwordVisible by remember { mutableStateOf(false) }

    var message by remember { mutableStateOf<String?>(null) }
    var isError by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()

    fun showMessage(msg: String, error: Boolean = false) {
        message = msg
        isError = error
    }

    Column(
        modifier = Modifier.fillMaxSize().padding(32.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = if (isLoginMode) "Accedi a Monetix" else "Crea un Account",
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold
        )
        Spacer(modifier = Modifier.height(32.dp))

        // --- CAMPO EMAIL ---
        OutlinedTextField(
            value = email,
            onValueChange = { email = it },
            label = { Text("Email") },
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Email,
                capitalization = KeyboardCapitalization.None,
                autoCorrectEnabled = false
            )
        )
        Spacer(modifier = Modifier.height(16.dp))

        // --- CAMPO PASSWORD (CON FIX PER IL "!") ---
        OutlinedTextField(
            value = password,
            onValueChange = { password = it },
            label = { Text("Password") },
            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),

            // 🚀 FIX: Usiamo KeyboardType.Password.
            // Questo disattiva forzatamente i suggerimenti del browser che causano il reset con i simboli come !
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Password,
                capitalization = KeyboardCapitalization.None,
                autoCorrectEnabled = false
            ),

            trailingIcon = {
                val image = if (passwordVisible) Icons.Outlined.VisibilityOff else Icons.Outlined.Visibility
                IconButton(onClick = { passwordVisible = !passwordVisible }) {
                    Icon(imageVector = image, contentDescription = null)
                }
            },
            modifier = Modifier.fillMaxWidth(),
            singleLine = true
        )

        // Password dimenticata
        if (isLoginMode) {
            TextButton(
                onClick = {
                    val cleanEmail = email.replace("\\s+".toRegex(), "").replace("\u00A0", "").replace("\u200B", "").lowercase()
                    if (cleanEmail.isBlank()) {
                        showMessage("Inserisci l'email per il recupero.", true)
                        return@TextButton
                    }
                    scope.launch {
                        try {
                            Firebase.auth.sendPasswordResetEmail(cleanEmail)
                            showMessage("Email di recupero inviata!", false)
                        } catch (e: Exception) {
                            showMessage("Errore nell'invio email.", true)
                        }
                    }
                },
                modifier = Modifier.align(Alignment.End)
            ) {
                Text("Password dimenticata?", fontSize = 12.sp)
            }
        } else {
            Spacer(modifier = Modifier.height(24.dp))
        }

        // Messaggi di errore/successo
        if (message != null) {
            Text(
                text = message!!,
                color = if (isError) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary,
                textAlign = TextAlign.Center,
                style = MaterialTheme.typography.bodySmall
            )
            Spacer(modifier = Modifier.height(16.dp))
        }

        // --- BOTTONE ACCEDI / REGISTRATI ---
        Button(
            onClick = {
                scope.launch {
                    isLoading = true
                    message = null
                    try {
                        val cleanEmail = email.replace("\\s+".toRegex(), "").replace("\u00A0", "").replace("\u200B", "").lowercase()
                        val cleanPassword = password.trim()

                        if (isLoginMode) {
                            Firebase.auth.signInWithEmailAndPassword(cleanEmail, cleanPassword)
                            onLoginSuccess()
                        } else {
                            Firebase.auth.createUserWithEmailAndPassword(cleanEmail, cleanPassword)
                            try { Firebase.auth.currentUser?.sendEmailVerification() } catch (e: Exception) {}
                            showMessage("Registrazione OK! Ora puoi accedere.", false)
                            Firebase.auth.signOut()
                            isLoginMode = true
                        }
                    } catch (e: Exception) {
                        val errMsg = e.message ?: ""
                        val cleanMsg = when {
                            errMsg.contains("invalid-credential", ignoreCase = true) || errMsg.contains("wrong-password", ignoreCase = true) -> "Email o Password errata."
                            errMsg.contains("user-not-found", ignoreCase = true) -> "Account non trovato. Registrati prima."
                            errMsg.contains("invalid-email", ignoreCase = true) || errMsg.contains("badly formatted", ignoreCase = true) -> "Indirizzo email non valido."
                            errMsg.contains("network", ignoreCase = true) -> "Errore di connessione a internet."
                            else -> "Errore di accesso: $errMsg"
                        }
                        showMessage(cleanMsg, true)
                    } finally { isLoading = false }
                }
            },
            modifier = Modifier.fillMaxWidth(),
            enabled = !isLoading && email.isNotBlank() && password.isNotBlank()
        ) {
            Text(if (isLoading) "ATTENDERE..." else if (isLoginMode) "ACCEDI" else "REGISTRATI")
        }

        Spacer(modifier = Modifier.height(16.dp))

        // --- 🚀 NOVITÀ: BOTTONE GOOGLE ---
        OutlinedButton(
            onClick = {
                scope.launch {
                    isLoading = true
                    try {
                        // 🚀 CHIAMIAMO LA FUNZIONE PONTE INVECE DI signInWithPopup DIRETTAMENTE
                        signInWithGoogleCommon(Firebase.auth)

                        onLoginSuccess()
                    } catch (e: Exception) {
                        showMessage("Errore Google: ${e.message}", true)
                    } finally { isLoading = false }
                }
            },
            modifier = Modifier.fillMaxWidth(),
            enabled = !isLoading
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text("G  ", fontWeight = FontWeight.Bold, color = Color(0xFF4285F4)) // Simbolo Google "finto"
                Text("ACCEDI CON GOOGLE")
            }
        }

        Spacer(modifier = Modifier.height(24.dp))

        TextButton(
            onClick = {
                isLoginMode = !isLoginMode
                message = null
                password = ""
            },
            enabled = !isLoading
        ) {
            Text(if (isLoginMode) "Non hai un account? Registrati" else "Hai già un account? Accedi")
        }
    }
}